import { getTokenFromCookie, getApiUrl } from './constants';
import { getInstallmentBearerToken } from './scopedAuth';
import { toast } from 'react-toastify';

/**
 * Get authentication headers with bearer token
 */
const getAuthHeaders = (token = null) => {
  const authToken = getInstallmentBearerToken(token || getTokenFromCookie());
  return {
    'Content-Type': 'application/json',
    ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
  };
};

/**
 * Create a new installment payment plan
 * @param {string} paymentMethodId - Stripe payment method ID
 * @param {number} totalAmount - Total amount to be paid
 * @param {number} totalInstallments - Number of installments (3 or 6)
 * @param {string} currency - Currency code (USD, GBP, EUR, CAD)
 * @param {object} metadata - Additional metadata (course info, user info, etc.)
 * @returns {Promise<object>} Response with installment details and clientSecret
 */
export const createInstallmentPayment = async ({
  paymentMethodId,
  totalAmount,
  totalInstallments,
  currency = 'USD',
  metadata = {},
  token = null,
}) => {
  try {
    const authToken = getInstallmentBearerToken(token || getTokenFromCookie());
    if (!authToken) {
      return {
        success: false,
        error: 'Authentication required. Please log in.'
      };
    }

    const response = await fetch(getApiUrl('installment-payments'), {
      method: 'POST',
      headers: getAuthHeaders(authToken),
      body: JSON.stringify({
        paymentMethodId,
        totalAmount,
        totalInstallments,
        currency,
        metadata
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to create installment payment:', data);
      // Handle Strapi error structure: { data: null, error: { message, status, name } }
      const errorMessage = data.error?.message || data.message || data.error || 'Failed to create installment payment';
      return {
        success: false,
        error: errorMessage,
        message: errorMessage // Alias for backwards compatibility
      };
    }
    
    return {
      success: true,
      data: {
        installmentRecordId: data.data.installmentRecordId,
        subscriptionId: data.data.subscriptionId,
        clientSecret: data.data.clientSecret,
        installmentAmount: data.data.installmentAmount,
        nextPaymentDate: data.data.nextPaymentDate
      }
    };
  } catch (error) {
    console.error('Error creating installment payment:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Get all installment payments for the current user
 * @returns {Promise<object>} Response containing array of installments
 */
export const getInstallmentPayments = async () => {
  try {
    const token = getTokenFromCookie();
    const authToken = getInstallmentBearerToken(token);
    if (!authToken) {
      return {
        success: false,
        error: 'Authentication required. Please log in.'
      };
    }

    const response = await fetch(getApiUrl('installment-payments'), {
      method: 'GET',
      headers: getAuthHeaders(authToken)
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to fetch installment payments:', data);
      const errorMessage = data.error?.message || data.message || data.error || 'Failed to fetch installment payments';
      return {
        success: false,
        error: errorMessage
      };
    }
    
    return {
      success: true,
      data: data.data || []
    };
  } catch (error) {
    console.error('Error fetching installment payments:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Get details of a specific installment payment
 * @param {string} installmentId - Installment payment ID
 * @returns {Promise<object>} Response containing installment details
 */
export const getInstallmentDetails = async (installmentId) => {
  try {
    const token = getTokenFromCookie();
    const authToken = getInstallmentBearerToken(token);
    if (!authToken) {
      return {
        success: false,
        error: 'Authentication required. Please log in.'
      };
    }

    const response = await fetch(getApiUrl(`installment-payments/${installmentId}`), {
      method: 'GET',
      headers: getAuthHeaders(authToken)
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to fetch installment details:', data);
      const errorMessage = data.error?.message || data.message || data.error || 'Failed to fetch installment details';
      return {
        success: false,
        error: errorMessage
      };
    }
    
    return {
      success: true,
      data: data.data
    };
  } catch (error) {
    console.error('Error fetching installment details:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Cancel an installment payment plan (only if completed)
 * @param {string} installmentId - Installment payment ID
 * @returns {Promise<object>} Response indicating success or failure
 */
export const cancelInstallmentPayment = async (installmentId) => {
  try {
    const token = getTokenFromCookie();
    const authToken = getInstallmentBearerToken(token);
    if (!authToken) {
      return {
        success: false,
        error: 'Authentication required. Please log in.'
      };
    }

    // First check if the installment is completed
    const detailsResult = await getInstallmentDetails(installmentId);
    if (!detailsResult.success) {
      return detailsResult;
    }

    if (detailsResult.data.status !== 'completed') {
      return {
        success: false,
        error: 'You can only cancel installment plans after all payments have been completed.'
      };
    }

    const response = await fetch(getApiUrl(`installment-payments/${installmentId}/cancel`), {
      method: 'POST',
      headers: getAuthHeaders(authToken)
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to cancel installment payment:', data);
      const errorMessage = data.error?.message || data.message || data.error || 'Failed to cancel installment payment';
      return {
        success: false,
        error: errorMessage
      };
    }
    
    return {
      success: true,
      message: 'Installment plan canceled successfully'
    };
  } catch (error) {
    console.error('Error canceling installment payment:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Check if a payment method has active installments
 * @param {string} paymentMethodId - Stripe payment method ID
 * @returns {Promise<object>} Response indicating if card has active installments
 */
export const checkActiveInstallments = async (paymentMethodId) => {
  try {
    const token = getTokenFromCookie();
    const authToken = getInstallmentBearerToken(token);
    if (!authToken) {
      return {
        success: false,
        error: 'Authentication required. Please log in.'
      };
    }

    const response = await fetch(
      getApiUrl(`installment-payments/check-active?paymentMethodId=${paymentMethodId}`),
      {
        method: 'GET',
        headers: getAuthHeaders(authToken)
      }
    );

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to check active installments:', data);
      const errorMessage = data.error?.message || data.message || data.error || 'Failed to check active installments';
      return {
        success: false,
        error: errorMessage
      };
    }
    
    return {
      success: true,
      hasActiveInstallments: data.hasActiveInstallments,
      activeInstallments: data.activeInstallments || []
    };
  } catch (error) {
    console.error('Error checking active installments:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Format installment data for display
 * @param {object} installment - Raw installment data
 * @returns {object} Formatted installment data
 */
export const formatInstallment = (installment) => {
  if (!installment) return null;

  return {
    id: installment.id,
    courseTitle: installment.courseTitle || 'Course',
    totalAmount: installment.totalAmount,
    installmentAmount: installment.installmentAmount,
    completedInstallments: installment.completedInstallments || 0,
    totalInstallments: installment.totalInstallments,
    status: installment.status,
    nextPaymentDate: installment.nextPaymentDate,
    currency: installment.currency || 'USD',
    progress: `${installment.completedInstallments || 0}/${installment.totalInstallments}`,
    progressPercentage: Math.round(
      ((installment.completedInstallments || 0) / installment.totalInstallments) * 100
    ),
    isComplete: installment.status === 'completed',
    isActive: installment.status === 'active',
    isCanceled: installment.status === 'canceled'
  };
};

/**
 * Validate installment creation data
 * @param {object} data - Installment creation data
 * @returns {object} Validation result
 */
export const validateInstallmentData = (data) => {
  const errors = [];

  if (!data.paymentMethodId) {
    errors.push('Payment method is required');
  }

  if (!data.totalAmount || data.totalAmount <= 0) {
    errors.push('Total amount must be greater than 0');
  }

  // Allow any installment count between 2 and 12 months
  if (!data.totalInstallments || data.totalInstallments < 2 || data.totalInstallments > 12) {
    errors.push('Installments must be between 2 and 12 months');
  }

  if (!data.currency) {
    errors.push('Currency is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Helper to display user-friendly error messages
 * @param {string} error - Error message
 */
export const displayInstallmentError = (error) => {
  const userFriendlyErrors = {
    'Authentication required': 'Please log in to create an installment plan',
    'Payment method is required': 'Please select a payment method',
    'Network error': 'Connection error. Please check your internet and try again',
    'Failed to create': 'Unable to create installment plan. Please try again',
    'card_declined': 'Your card was declined. Please try another card',
    'insufficient_funds': 'Insufficient funds. Please try another card'
  };

  for (const [key, message] of Object.entries(userFriendlyErrors)) {
    if (error.toLowerCase().includes(key.toLowerCase())) {
      toast.error(message);
      return;
    }
  }

  // Default error
  toast.error(error || 'An error occurred. Please try again');
};

/**
 * Poll the backend until the webhook-confirmed installment record reflects
 * the successful charge. Only used for the scoped new-user flow.
 */
export const waitForInstallmentBackendConfirmation = async (
  installmentId,
  token = null,
  { timeoutMs = 30000, intervalMs = 1500 } = {}
) => {
  const authToken = getInstallmentBearerToken(token || getTokenFromCookie());

  if (!authToken) {
    return {
      success: false,
      error: 'Authentication required. Please log in.'
    };
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const response = await fetch(getApiUrl(`installment-payments/${installmentId}`), {
      method: 'GET',
      headers: getAuthHeaders(authToken),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error?.message || data.message || 'Failed to confirm installment payment',
      };
    }

    const installment = data.data || data;
    const completedCount = Number(installment?.completedInstallments || 0);
    const status = installment?.status;

    if ((status === 'active' && completedCount >= 1) || status === 'completed') {
      return {
        success: true,
        data: installment,
      };
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return {
    success: false,
    error: 'Payment was processed, but backend confirmation is still pending. Please refresh in a moment.'
  };
};

// ---------------------------------------------------------------------------
// REMOVED: createGuestSetupIntent, storeGuestCheckoutEmail,
//          getGuestCheckoutEmail, clearGuestCheckoutData
//
// These helpers supported a guest-checkout card-saving flow that is no longer
// used.  The registration flow now issues a scopedJwt so the user's card is
// saved directly against their authenticated Stripe customer.  Any guest-card
// merge happens in emailVerificationSuccess.js after the full JWT is received.
// ---------------------------------------------------------------------------

/**
 * Merge guest cards with authenticated user account
 * @param {string} jwtToken - User's JWT authentication token
 * @param {string} userEmail - User's email address
 * @returns {Promise<object>} Merge result
 */
export const mergeGuestCards = async (jwtToken, userEmail) => {
  try {
    // Prefer a locally-stored guest email if one exists; fall back to the
    // email passed in (or let the server derive it from the JWT).
    const guestEmail = (() => {
      try { return localStorage.getItem('guestCheckoutEmail'); } catch { return null; }
    })();
    const emailToMerge = guestEmail || userEmail;
    
    if (!emailToMerge) {
      return {
        success: false,
        message: 'No email found for merge'
      };
    }


    const response = await fetch(getApiUrl('guest-customer/merge'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: JSON.stringify({ email: emailToMerge })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      const transferredCount = result.data?.paymentMethodsTransferred || 0;
      
      if (transferredCount > 0) {
        
                // Show success notification
        toast.success(
          `Success! ${transferredCount} saved card(s) added to your account.`,
          { autoClose: 4000 }
        );

        // Clear locally-stored guest email now that the merge is done
        try { localStorage.removeItem('guestCheckoutEmail'); localStorage.removeItem('guestCheckoutDate'); } catch { /* ignore */ }

        return {
          success: true,
          data: result.data,
          message: `${transferredCount} card(s) transferred`
        };
      } else {
        // Clear the stored email anyway since we attempted merge
        try { localStorage.removeItem('guestCheckoutEmail'); localStorage.removeItem('guestCheckoutDate'); } catch { /* ignore */ }
        return {
          success: true,
          data: result.data,
          message: 'No cards to transfer'
        };
      }
    } else {
      // Don't throw error, just clear the data and continue
      try { localStorage.removeItem('guestCheckoutEmail'); localStorage.removeItem('guestCheckoutDate'); } catch { /* ignore */ }
      return {
        success: false,
        message: result.data?.message || 'No cards to merge'
      };
    }
  } catch (error) {
    console.error('Error merging guest cards:', error);
    // Don't throw - merge failure shouldn't prevent login/signup
    return {
      success: false,
      error: error.message || 'Failed to merge guest cards'
    };
  }
};
